import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherX } from "@subframe/core";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import type { SummaryGroup } from "./compliance-task-summary";

// Confirmation copy (NAU-336) — varies by mode.
const COPY = {
  create: {
    title: "Create this compliance task?",
    alertTitle: "What happens when you create this task?",
    alertDescription:
      "The task is created and its first instance is scheduled for the first due date. You can edit or deactivate it at any time.",
    cancel: "Cancel",
    confirm: "Create task",
    confirming: "Creating...",
  },
  edit: {
    title: "Save these changes?",
    alertTitle: "What happens when you save?",
    alertDescription:
      "Changes affect future occurrences only. The currently open instance keeps its due date.",
    cancel: "Cancel",
    confirm: "Save changes",
    confirming: "Saving...",
  },
};

interface ConfirmComplianceTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: SummaryGroup[];
  isSaving: boolean;
  onConfirm: () => void;
  mode?: "create" | "edit";
}

export function ConfirmComplianceTaskDialog({
  open,
  onOpenChange,
  summary,
  isSaving,
  onConfirm,
  mode = "create",
}: ConfirmComplianceTaskDialogProps) {
  // Don't let Escape / overlay / the X dismiss the dialog mid-create.
  const handleOpenChange = (next: boolean) => {
    if (!next && isSaving) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="p-0 sm:max-w-[576px]" showCloseButton={false}>
        <DialogTitle className="sr-only">{COPY[mode].title}</DialogTitle>
        <div className="flex h-full w-full flex-col items-start bg-default-background">
          <div className="flex w-full flex-col items-start gap-4 px-8 py-6 overflow-auto">
            <div className="flex w-full items-center justify-between py-2">
              <span
                className="text-heading-2 font-heading-2 text-primary-navy-800"
                aria-hidden="true"
              >
                {COPY[mode].title}
              </span>
              <IconButton
                variant="neutral-tertiary"
                icon={<FeatherX />}
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              />
            </div>

            <div className="flex w-full flex-col items-start gap-4">
              {summary.map((group) => (
                <div
                  key={group.title}
                  className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4"
                >
                  <span className="text-body-bold font-body-bold text-default-font">
                    {group.title}
                  </span>
                  {group.rows.map((row) => (
                    <div
                      key={row.label}
                      className="flex w-full items-start justify-between gap-4"
                    >
                      <span className="text-caption font-caption text-subtext-color">
                        {row.label}
                      </span>
                      <span className="text-caption font-caption text-default-font text-right">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <Alert
              variant="brand"
              title={COPY[mode].alertTitle}
              description={COPY[mode].alertDescription}
            />

            <div className="flex w-full items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                size="large"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                {COPY[mode].cancel}
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={onConfirm}
                disabled={isSaving}
              >
                {isSaving ? COPY[mode].confirming : COPY[mode].confirm}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
